import { useGLTF } from '@react-three/drei';
import { useEffect, useState, useRef } from 'react';
import * as THREE from 'three';
import { ModelConfig } from '../types/models';
import { DRACO_DECODER_PATH } from '../lib/dracoLoader';

interface UseModelLoaderOptions {
  config: ModelConfig;
  onModelReady?: () => void;
}

interface UseModelLoaderResult {
  scene: THREE.Object3D;
  animations: THREE.AnimationClip[];
  cameras: THREE.Camera[];
}

/**
 * Loads a GLB model and reports when the scene and animations are ready.
 *
 * Responsibilities:
 * - useGLTF model loading with Draco decoding support (GStatic CDN 1.5.7)
 * - 10-second WASM timeout with automatic fallback to non-Draco loading
 * - onModelReady callback
 * - returning raw scene assets for runtime systems to bind
 */
export function useModelLoader({
  config,
  onModelReady,
}: UseModelLoaderOptions): UseModelLoaderResult {
  const [dracoFailed, setDracoFailed] = useState(false);
  const modelReady = useRef(false);
  const currentPathRef = useRef(config.modelPath);

  // Reset state when model path changes (prevents stale ref from previous load)
  useEffect(() => {
    if (currentPathRef.current !== config.modelPath) {
      currentPathRef.current = config.modelPath;
      modelReady.current = false;
      setDracoFailed(false);
    }
  }, [config.modelPath]);

  const gltf = useGLTF(
    config.modelPath,
    dracoFailed ? false : DRACO_DECODER_PATH
  );

  // 10-second WASM timeout — if the model hasn't loaded, fall back to non-Draco
  useEffect(() => {
    if (dracoFailed || modelReady.current) return;

    const currentPath = config.modelPath;
    const timeoutId = setTimeout(() => {
      // Guard: only fire if this timeout is still for the current model
      if (!modelReady.current && currentPathRef.current === currentPath) {
        console.error('[DRACOLoader] WASM decoder timeout after 10s');
        setDracoFailed(true);
      }
    }, 10_000);

    return () => clearTimeout(timeoutId);
  }, [dracoFailed, config.modelPath]);

  useEffect(() => {
    if (!gltf.scene) return;
    modelReady.current = true;
    onModelReady?.();
  }, [gltf.scene, onModelReady]);

  return {
    scene: gltf.scene,
    animations: gltf.animations ?? [],
    cameras: (gltf.cameras ?? []) as THREE.Camera[],
  };
}
